/**
 * In-App Purchase Service per BustaControllo
 * Usa cordova-plugin-purchase v13 (CdvPurchase) via Capacitor Cordova bridge
 * Supporta iOS (Apple App Store) e Android (Google Play)
 */

import axios from 'axios';
import { API_URL } from '../config';

// Product IDs per i pacchetti crediti
export const IAP_PRODUCTS = {
  CREDITS_5:  'com.bustacontrollo.app.credits5',
  CREDITS_10: 'com.bustacontrollo.app.credits10',
  CREDITS_20: 'com.bustacontrollo.app.credits20',
  CREDITS_50: 'com.bustacontrollo.app.credits50',
};

// Mappa productId → numero crediti
const CREDITS_MAP = {
  'com.bustacontrollo.app.credits5':  5,
  'com.bustacontrollo.app.credits10': 10,
  'com.bustacontrollo.app.credits20': 20,
  'com.bustacontrollo.app.credits50': 50,
};

const isNative = () => !!(window.Capacitor && window.Capacitor.isNativePlatform());

/**
 * Ritorna la piattaforma corrente: 'ios' | 'android' | null
 */
const getNativePlatform = () => {
  const p = window.Capacitor?.getPlatform?.();
  if (p === 'ios') return 'ios';
  if (p === 'android') return 'android';
  return null;
};

/**
 * Ritorna il Platform CdvPurchase per la piattaforma corrente
 */
const getCdvPlatform = () => {
  if (!window.CdvPurchase) return null;
  const { Platform } = window.CdvPurchase;
  return getNativePlatform() === 'android' ? Platform.GOOGLE_PLAY : Platform.APPLE_APP_STORE;
};

/**
 * Prodotti di fallback con prezzi fissi (mostrati se store non disponibile)
 */
function getFallbackProducts() {
  return [
    { productId: IAP_PRODUCTS.CREDITS_5,  title: '5 Crediti',  price: '€4,99',  priceValue: 4.99,  credits: 5,  popular: false },
    { productId: IAP_PRODUCTS.CREDITS_10, title: '10 Crediti', price: '€8,99',  priceValue: 8.99,  credits: 10, popular: true  },
    { productId: IAP_PRODUCTS.CREDITS_20, title: '20 Crediti', price: '€13,99', priceValue: 13.99, credits: 20, popular: false },
    { productId: IAP_PRODUCTS.CREDITS_50, title: '50 Crediti', price: '€28,99', priceValue: 28.99, credits: 50, popular: false },
  ];
}

let storeInitialized = false;
let storeInitPromise = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function refreshStoreProducts(store) {
  if (typeof store.update !== 'function') return;
  try {
    await store.update();
  } catch (e) {
    console.warn('[IAP] Store update non riuscito:', e);
  }
}

/**
 * Inizializza il CdvPurchase store (una sola volta)
 */
function initStore() {
  if (storeInitialized) return Promise.resolve(true);
  if (storeInitPromise) return storeInitPromise;

  storeInitPromise = new Promise((resolve) => {
    if (!window.CdvPurchase) {
      console.log('[IAP] CdvPurchase non disponibile');
      resolve(false);
      return;
    }

    try {
      const { store, ProductType } = window.CdvPurchase;
      const cdvPlatform = getCdvPlatform();

      if (!cdvPlatform) {
        console.warn('[IAP] Piattaforma non riconosciuta');
        resolve(false);
        return;
      }

      // Registra i prodotti come CONSUMABLE per la piattaforma corrente
      store.register(
        Object.values(IAP_PRODUCTS).map((id) => ({
          id,
          type: ProductType.CONSUMABLE,
          platform: cdvPlatform,
        }))
      );

      // Inizializza lo store per la piattaforma corrente
      store.initialize([cdvPlatform]).then(async () => {
        await refreshStoreProducts(store);
        storeInitialized = true;
        console.log('[IAP] Store inizializzato per', cdvPlatform);
        resolve(true);
      }).catch((err) => {
        console.error('[IAP] Errore init store:', err);
        resolve(false);
      });
    } catch (e) {
      console.error('[IAP] Errore setup store:', e);
      resolve(false);
    }
  });

  return storeInitPromise;
}

/**
 * Recupera i prodotti disponibili dallo store
 * Fallback a prezzi fissi se store non disponibile
 */
export async function getProducts() {
  if (!isNative()) {
    return getFallbackProducts();
  }

  try {
    const ok = await initStore();
    if (!ok || !window.CdvPurchase) return getFallbackProducts();

    const cdvPlatform = getCdvPlatform();

    const { store } = window.CdvPurchase;
    for (let attempt = 1; attempt <= 6; attempt += 1) {
      const products = Object.values(IAP_PRODUCTS)
        .map((id) => store.get(id, cdvPlatform))
        .filter((p) => p && p.offers && p.offers.length > 0)
        .map((p) => {
          const phase = p.offers[0]?.pricingPhases?.[0];
          return {
            productId: p.id,
            title: p.title || `${CREDITS_MAP[p.id]} Crediti`,
            price: phase?.price || '',
            priceValue: phase?.priceMicros ? phase.priceMicros / 1_000_000 : 0,
            credits: CREDITS_MAP[p.id] || 0,
            popular: p.id === IAP_PRODUCTS.CREDITS_10,
          };
        });

      if (products.length > 0) return products;

      await refreshStoreProducts(store);
      await sleep(1000);
    }

    console.warn('[IAP] Nessun prodotto disponibile dallo store, uso fallback UI');
    return getFallbackProducts();
  } catch (e) {
    console.error('[IAP] getProducts error:', e);
    return getFallbackProducts();
  }
}

/**
 * Avvia l'acquisto di un pacchetto crediti tramite IAP (iOS o Android)
 */
export async function purchaseCredits(productId, authToken) {
  if (!isNative()) {
    return { success: false, error: 'not_native' };
  }

  try {
    const ok = await initStore();
    if (!ok || !window.CdvPurchase) {
      return { success: false, error: 'store_unavailable' };
    }

    const { store } = window.CdvPurchase;
    const cdvPlatform = getCdvPlatform();
    await refreshStoreProducts(store);
    let product = store.get(productId, cdvPlatform);

    if (!product || !product.offers || product.offers.length === 0) {
      await sleep(1000);
      await refreshStoreProducts(store);
      product = store.get(productId, cdvPlatform);
    }

    if (!product || !product.offers || product.offers.length === 0) {
      console.warn('[IAP] Prodotto non disponibile nello store:', productId, cdvPlatform);
      return {
        success: false,
        error: 'product_unavailable',
        details: `${productId} non disponibile su ${cdvPlatform}`,
      };
    }

    return new Promise((resolve) => {
      let resolved = false;
      let unsubApproved = null;
      let timeoutHandle = null;

      const cleanup = () => {
        if (typeof unsubApproved === 'function') {
          unsubApproved();
        }
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
        }
      };

      const finish = (result) => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(result);
        }
      };

      // Timeout di 60 secondi per evitare che il listener rimanga sospeso forever
      timeoutHandle = setTimeout(() => {
        if (!resolved) {
          console.warn('[IAP] Timeout: acquisto non completato entro 60 secondi');
          finish({ success: false, error: 'purchase_timeout' });
        }
      }, 60000);

      // Listener per transazione approvata
      unsubApproved = store.when().approved(async (transaction) => {
        const isOurs = transaction.products?.some((p) => p.id === productId);
        if (!isOurs) return;

        try {
          const txId =
            transaction.transactionId ||
            transaction.nativePurchase?.transactionIdentifier ||
            transaction.id;

          if (!txId) {
            await transaction.finish().catch(() => {});
            finish({ success: false, error: 'validation_error', details: 'No transaction ID' });
            return;
          }

          const result = await validateAndAddCredits(txId, productId, authToken);
          await transaction.finish();
          finish(result);
        } catch (e) {
          await transaction.finish().catch(() => {});
          console.error('[IAP] Validazione transazione fallita:', e);
          finish({ success: false, error: 'validation_error' });
        }
      });

      // Avvia l'ordine
      const offer = product.offers[0];
      store.order(offer).catch((e) => {
        const code = e?.code || e?.message || '';
        if (
          code === window.CdvPurchase?.ErrorCode?.PAYMENT_CANCELLED ||
          String(code).includes('cancel')
        ) {
          finish({ success: false, error: 'cancelled' });
        } else {
          console.error('[IAP] Errore order:', e);
          finish({ success: false, error: 'purchase_error', details: String(code || 'order_failed') });
        }
      });
    });
  } catch (e) {
    console.error('[IAP] purchaseCredits error:', e);
    return { success: false, error: 'purchase_error' };
  }
}

/**
 * Valida l'acquisto sul backend e aggiunge i crediti all'account
 */
async function validateAndAddCredits(transactionId, productId, authToken) {
  try {
    const credits = CREDITS_MAP[productId] || 0;
    const platform = getNativePlatform() || 'ios';
    const response = await axios.post(
      `${API_URL}/payments/validate-iap`,
      {
        product_id: productId,
        transaction_id: transactionId,
        credits,
        platform,
      },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    if (response.data.success) {
      return {
        success: true,
        credits: response.data.credits,
        newBalance: response.data.new_balance,
      };
    }
    return { success: false, error: 'validation_failed' };
  } catch (e) {
    console.error('[IAP] validateAndAddCredits error:', e);
    return { success: false, error: 'validation_error' };
  }
}

/**
 * Ripristina gli acquisti precedenti (richiesto da App Store/Play Store guidelines)
 */
export async function restorePurchases(authToken) {
  if (!isNative()) return { success: false };

  try {
    const ok = await initStore();
    if (!ok || !window.CdvPurchase) return { success: false };

    await window.CdvPurchase.store.restorePurchases();
    return { success: true };
  } catch (e) {
    console.error('[IAP] restorePurchases error:', e);
    return { success: false };
  }
}
